import { Injectable } from '@angular/core'
import { BehaviorSubject, combineLatest, EMPTY } from 'rxjs'
import { MbConstants } from '../../shared/mb-constants'
import { CommandService, EventService, UserIdService, WindowRef } from '@spartacus/core'
import { MultiCartService } from '@spartacus/cart/base/core'
import { MbCheckoutConnector } from '../connectors/checkout'
import { catchError, switchMap, take, tap } from 'rxjs/operators'
import { LAUNCH_CALLER, LaunchDialogService } from '@spartacus/storefront'
import { MbCheckoutNavigationService } from './mb-checkout-navigation.service'
import { OrderConnector, OrderService } from '@spartacus/order/core'
import { ActiveCartFacade } from '@spartacus/cart/base/root'
import {
  CheckoutPlaceOrderWithPaymentFailEvent,
  CheckoutPlaceOrderWithPaymentSuccessEvent,
  CheckoutRedirectToPaymentEvent,
  CheckoutResetIngenicoPaymentEvent
} from '../../analytics/events/mb-checkout.events'
import { OrderPlacedEvent } from '@spartacus/order/root'

@Injectable({
  providedIn: 'root'
})
export class MbOrderService extends OrderService {

  constructor (protected activeCartService: ActiveCartFacade,
               protected userIdService: UserIdService,
               protected commandService: CommandService,
               protected eventService: EventService,
               protected mbCheckoutNavigationService: MbCheckoutNavigationService,
               protected mbCheckoutConnector: MbCheckoutConnector,
               protected orderConnector: OrderConnector,
               protected launchDialogService: LaunchDialogService,
               protected winRef: WindowRef,
               protected multiCartService: MultiCartService) {
    super(activeCartService, userIdService, commandService, orderConnector, eventService)
  }
  constants = MbConstants
  private placeOrderTrigger = new BehaviorSubject(false)
  get isPlaceOrderTriggered () {
    return this.placeOrderTrigger.asObservable()
  }

  triggerPlaceOrder (): void {
    this.placeOrderTrigger.next(true)
  }

  stopPlaceOrderTrigger (): void {
    this.placeOrderTrigger.next(false)
  }

  redirectToPaymentPage (termsChecked: boolean) {
    this.commandService.create(() =>
      this.checkoutPreconditions().pipe(
        switchMap(([userId, cartId]) =>
          this.mbCheckoutConnector.redirectToPaymentPage(userId, cartId, termsChecked).pipe(
            tap(data => {
              const redirectUrl = data?.redirectURL
              if (redirectUrl) {
                this.launchDialogService.clear(LAUNCH_CALLER.PLACE_ORDER_SPINNER)
                this.winRef.location.href = redirectUrl
              }
              this.eventService.dispatch({ isSuccessful: true }, CheckoutRedirectToPaymentEvent)
            }),
            catchError(error => {
              this.eventService.dispatch({ isSuccessful: false, error: error }, CheckoutRedirectToPaymentEvent)
              return EMPTY
            })
          ))
      )).execute(undefined)
  }

  placeOrderWithPayment (returnMac: string, hostedCheckoutId: string) {
    this.commandService.create(() =>
      this.checkoutPreconditions().pipe(
        switchMap(([userId, cartId]) =>
          this.mbCheckoutConnector.placeOrderWithPayment(userId, cartId, returnMac, hostedCheckoutId).pipe(
            tap(order => {
              if (order.returnStatus === this.constants.PLACE_ORDER_STATUS_SUCCESS) {
                this.eventService.dispatch({ isSuccessful: true }, CheckoutPlaceOrderWithPaymentSuccessEvent)
                this.eventService.dispatch({ order: order }, OrderPlacedEvent)
                this.mbCheckoutNavigationService.navigateToConfirmationPage(order).then(() => {
                  /**
                   * Create a new cart to prevent the issues from CWT-743 and CWT-768. Basically the removeCart triggers
                   * an infinite loop, which is caused inside of Spartacus' CartEffects. A LoadCart action is triggered
                   * for the cart with id "current". Two things happen now: This cart is set as the active cart, and a
                   * LoadCartSuccess action is triggered, which also results in settings this cart as the active one.
                   * This resulted in the active cart flip-flopping between "current" and an actual id, causing the
                   * infinite loop. This might just as well be an issue that should be adressed by Spartacus.
                   * The only way to properly solve this ourselves would be to debug ALL subscriptions to the
                   * activeCartId and find the one that causes the loop, which would take way too much time because there
                   * are so many.
                   *
                   * Preemptively creating a new cart and setting it as active breaks the cycle however, so that the cart
                   * that is being removed is not "current" anymore.
                   *
                   * However, we have to do this only AFTER the navigation to the checkout confirmation page has concluded,
                   * since the tracking on that page relies on the ActiveCartService still having the cart from the order.
                   */
                  this.multiCartService.createCart({ userId, extraData: { active: true } })
                  this.multiCartService.removeCart(cartId)
                })
              } else {
                const errorKey = order?.returnStatus?.toLowerCase() || 'failed'
                this.mbCheckoutNavigationService.navigateToReviewPage({ key: 'checkoutErrors.payment.' + errorKey })
                this.activeCartService.reloadActiveCart()
                this.eventService.dispatch({ returnStatus: order?.returnStatus }, CheckoutPlaceOrderWithPaymentFailEvent)
              }
            }),
            catchError(error => {
              this.eventService.dispatch({ error: error }, CheckoutPlaceOrderWithPaymentFailEvent)
              return EMPTY
            })
          ))
      )).execute(undefined)
  }

  resetIngenicoPayment () {
    this.commandService.create(() =>
      combineLatest([this.userIdService.takeUserId(), this.activeCartFacade.takeActiveCartId()]).pipe(
        take(1),
        switchMap(([userId, cartId]) => this.mbCheckoutConnector.resetIngenicoPayment(userId, cartId).pipe(
          tap(() => {
            this.mbCheckoutNavigationService.navigateToReviewPage()
            this.activeCartService.reloadActiveCart()
            this.eventService.dispatch({ isSuccessful: true }, CheckoutResetIngenicoPaymentEvent)
          }),
          catchError(error => {
            this.eventService.dispatch({ isSuccessful: false, error: error }, CheckoutResetIngenicoPaymentEvent)
            return EMPTY
          })
        ))
      )
    ).execute(undefined)
  }
}
